import { useMemo, useState } from "react";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import { StatCard } from "@/components/cards/StatCard";
import { ChartCard } from "@/components/cards/ChartCard";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { Badge } from "@/components/ui/Badge";
import { DatePicker } from "@/components/ui/DatePicker";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { SimpleBarChart } from "@/components/charts/SimpleBarChart";
import { DonutChart } from "@/components/charts/DonutChart";
import { EmptyChartState } from "@/components/charts/EmptyChartState";
import { useDashboardOverview } from "@/hooks/useDashboard";
import { formatCompactINR, formatNumber, formatPercent } from "@/utils/format";
import {
  type AnalyticsViewMode,
  CHART_COLORS,
  type BreakdownMetric,
  type GrowthMetric,
  getDaysAgoIso,
  isGrowthSeriesEmpty,
  isBreakdownSeriesEmpty,
  resolveBreakdownSeries,
  resolveGrowthSeries,
} from "./dashboardCharts";

export default function Dashboard() {
  const overview = useDashboardOverview();

  const todayIso = useMemo(() => getDaysAgoIso(0), []);
  const thirtyDaysAgoIso = useMemo(() => getDaysAgoIso(30), []);

  const [viewMode, setViewMode] = useState<AnalyticsViewMode>("single");
  const [selectedDate, setSelectedDate] = useState<string>(todayIso);
  const [growthMetric, setGrowthMetric] = useState<GrowthMetric>("drivers");
  const [breakdownMetric, setBreakdownMetric] = useState<BreakdownMetric>("rides");

  // Support ?emptyCharts=true parameter for instant visual testing of empty states
  const forceEmpty = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("emptyCharts") === "true";
  }, []);

  const daysData = forceEmpty ? [] : overview.data?.analytics?.days;

  const growthSeries = useMemo(() => {
    return resolveGrowthSeries(daysData, viewMode, selectedDate, growthMetric);
  }, [daysData, viewMode, selectedDate, growthMetric]);

  const breakdownSeries = useMemo(() => {
    return resolveBreakdownSeries(
      daysData,
      viewMode,
      selectedDate,
      breakdownMetric,
      forceEmpty ? undefined : overview.data?.kpis,
    );
  }, [daysData, viewMode, selectedDate, breakdownMetric, overview.data?.kpis, forceEmpty]);

  const isGrowthEmpty = isGrowthSeriesEmpty(growthSeries);
  const isBreakdownEmpty = isBreakdownSeriesEmpty(breakdownSeries);

  return (
    <PageContainer title="Dashboard" description="Live operations overview, refreshed every 30s.">
      {overview.isLoading && <LoadingState label="Loading dashboard…" />}
      {overview.isError && <ErrorState error={overview.error} onRetry={() => overview.refetch()} />}

      {overview.data && (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard value={formatNumber(overview.data.kpis.autosOnline)} label="Autos online" />
            <StatCard value={formatNumber(overview.data.kpis.ridesToday)} label="Rides today" />
            <StatCard
              value={formatPercent(overview.data.kpis.fulfilmentPct / 100)}
              label="Fulfilment"
            />
            <StatCard value={formatCompactINR(overview.data.kpis.gmvToday)} label="GMV" />
            <StatCard
              value={formatCompactINR(overview.data.kpis.commissionToday)}
              label="Commission"
              sublabel="10% rate"
              highlight
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="card p-4 lg:col-span-2">
              <h3 className="mb-3 text-sm font-semibold text-ink">At a glance</h3>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <StatCard value={formatNumber(overview.data.kpis.activeRides)} label="Active rides" />
                <StatCard value={formatNumber(overview.data.kpis.cancelledToday)} label="Cancelled today" />
                <StatCard value={formatNumber(overview.data.kpis.kycPending)} label="KYC pending" />
                <StatCard value={formatNumber(overview.data.kpis.sosOpen)} label="SOS open" />
              </div>
            </div>

            <div className="card p-4">
              <h3 className="mb-3 text-sm font-semibold text-ink">Alerts</h3>
              {overview.data.alerts.length === 0 && (
                <p className="text-xs text-ink-faint">All clear — no active alerts.</p>
              )}
              <ul className="flex flex-col gap-2">
                {overview.data.alerts.map((alert, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Badge tone={alert.severity === "danger" ? "danger" : "gold"}>
                      {alert.severity === "danger" ? (
                        <AlertTriangle className="h-3 w-3" />
                      ) : (
                        <ShieldAlert className="h-3 w-3" />
                      )}
                      {alert.severity === "danger" ? "Alert" : "Notice"}
                    </Badge>
                    <span className="text-ink-muted">{alert.text}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-ink-faint">
                {formatNumber(overview.data.kpis.openTickets)} open support tickets.
              </p>
            </div>
          </div>

          {/* Shared Mode Selector Bar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2">
            <div>
              <h3 className="text-sm font-semibold text-ink">Growth & Operations</h3>
              <p className="text-xs text-ink-muted">
                {viewMode === "single" && "Performance snapshot for a selected day"}
                {viewMode === "7d" && "Trends and operational totals over the last 7 days"}
                {viewMode === "30d" && "Trends and operational totals over the last 30 days"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <SegmentedControl<AnalyticsViewMode>
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { value: "single", label: "Single Day" },
                  { value: "7d", label: "Last 7 Days" },
                  { value: "30d", label: "Last 30 Days" },
                ]}
              />
              {viewMode === "single" && (
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  min={thirtyDaysAgoIso}
                  max={todayIso}
                  className="relative w-36"
                />
              )}
            </div>
          </div>

          {/* Growth & Breakdown Charts Grid */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* 1. Growth Bar Chart */}
            <ChartCard
              title={`Growth · ${growthMetric === "drivers" ? "Drivers" : "Customers"}`}
              action={
                <SegmentedControl<GrowthMetric>
                  value={growthMetric}
                  onChange={setGrowthMetric}
                  options={[
                    { value: "drivers", label: "Drivers" },
                    { value: "customers", label: "Customers" },
                  ]}
                />
              }
            >
              {isGrowthEmpty ? (
                <EmptyChartState message="No data yet for this period" />
              ) : (
                <SimpleBarChart
                  data={growthSeries ?? []}
                  color={growthMetric === "drivers" ? CHART_COLORS.drivers : CHART_COLORS.customers}
                  valueFormatter={formatNumber}
                  maxBarSize={viewMode === "single" ? 48 : viewMode === "7d" ? 32 : 14}
                />
              )}
            </ChartCard>

            {/* 2. Operations Breakdown Donut Chart */}
            <ChartCard
              title={`Breakdown · ${breakdownMetric === "rides" ? "Rides" : "Revenue"}`}
              action={
                <SegmentedControl<BreakdownMetric>
                  value={breakdownMetric}
                  onChange={setBreakdownMetric}
                  options={[
                    { value: "rides", label: "Rides" },
                    { value: "revenue", label: "Revenue" },
                  ]}
                />
              }
            >
              {isBreakdownEmpty ? (
                <EmptyChartState message="No data yet for this period" />
              ) : (
                <DonutChart
                  data={breakdownSeries}
                  valueFormatter={breakdownMetric === "revenue" ? formatCompactINR : formatNumber}
                />
              )}
            </ChartCard>
          </div>
        </>
      )}
    </PageContainer>
  );
}
